import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../../utils/api';

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : (d?.supabase_error ? `Supabase: ${d.supabase_error}` : fallback);
};

export const ResendVerificationButton = ({ userId, headers, testId }) => {
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/users/${userId}/resend-verification`, {}, { headers });
      toast.success('Verification email sent');
    } catch (e) { toast.error(errMsg(e, 'Failed to resend')); }
    setBusy(false);
  };
  return <Button variant="outline" size="sm" disabled={busy} onClick={resend} data-testid={testId || `resend-verify-${userId}`}>Resend verification</Button>;
};

const EditBusinessDialog = ({ biz, headers, onClose, onSaved }) => {
  const info = biz.business_info || {};
  const [form, setForm] = useState({
    company_name: info.company_name || '', org_number: info.org_number || '',
    contact_email: info.contact_email || '', contact_phone: info.contact_phone || '',
    address: info.address || '', postal_code: info.postal_code || '', city: info.city || '',
  });
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setError('');
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ''));
      await axios.put(`${API}/admin/businesses/${biz.id}`, payload, { headers });
      toast.success('Business updated'); onSaved();
    } catch (e) { setError(errMsg(e, 'Update failed')); }
  };
  const field = (k, label) => (
    <div><Label className="text-xs">{label}</Label><Input value={form[k]} onChange={set(k)} data-testid={`edit-biz-${k}`} /></div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-business-dialog">
        <DialogHeader><DialogTitle>Edit business</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {field('company_name', 'Company name')}{field('org_number', 'Org number')}
          {field('contact_email', 'Contact email')}{field('contact_phone', 'Contact phone')}
          {field('address', 'Address')}{field('postal_code', 'Postal code')}{field('city', 'City')}
          <div><Label className="text-xs">Swish (read-only)</Label><Input value={info.swish_merchant_number || biz.swish_payee_alias || '—'} disabled /></div>
        </div>
        <p className="text-xs text-muted-foreground">Login email ({biz.email}) can't be changed here — it is tied to the user's sign-in. Swish numbers can't be changed by admins.</p>
        {error && <p className="text-sm text-red-600" data-testid="edit-biz-error">{error}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="edit-biz-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DeleteBusinessDialog = ({ biz, headers, onClose, onDeleted }) => {
  const company = biz.business_info?.company_name || '';
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/businesses/${biz.id}`, { headers, data: { confirm_company_name: typed } });
      toast.success('Business deleted'); onDeleted();
    } catch (e) { toast.error(errMsg(e, 'Delete failed')); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="delete-business-dialog">
        <DialogHeader><DialogTitle className="text-red-700">Delete {company}</DialogTitle></DialogHeader>
        <p className="text-sm text-red-700 font-medium">This permanently deletes the business, all events and all payment history. This can't be undone.</p>
        <Label className="text-xs">Type the company name <strong>{company}</strong> to confirm</Label>
        <Input value={typed} onChange={e => setTyped(e.target.value)} data-testid="delete-biz-confirm-input" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" disabled={busy || typed !== company} onClick={remove} data-testid="delete-biz-confirm-btn">Delete permanently</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SuspendDialog = ({ biz, headers, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const suspend = async () => {
    try {
      await axios.post(`${API}/admin/businesses/${biz.id}/suspend`, { reason: reason || null }, { headers });
      toast.success('Business suspended'); onDone();
    } catch (e) { toast.error(errMsg(e, 'Suspend failed')); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="suspend-business-dialog">
        <DialogHeader><DialogTitle>Suspend {biz.business_info?.company_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Blocks all logins and makes every payment page unavailable until unsuspended.</p>
        <Textarea placeholder="Reason (optional)" value={reason} onChange={e => setReason(e.target.value)} data-testid="suspend-reason-input" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={suspend} data-testid="suspend-confirm-btn">Suspend</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const BusinessAdminActions = ({ biz, headers, onChanged }) => {
  const [dialog, setDialog] = useState(null);
  const suspended = biz.account_status === 'SUSPENDED';
  const unsuspend = async () => {
    try {
      await axios.post(`${API}/admin/businesses/${biz.id}/unsuspend`, {}, { headers });
      toast.success('Business unsuspended'); onChanged();
    } catch (e) { toast.error(errMsg(e, 'Unsuspend failed')); }
  };
  const close = () => setDialog(null);
  const done = () => { setDialog(null); onChanged(); };
  return (
    <div className="flex flex-wrap gap-2 pt-2" data-testid={`biz-admin-actions-${biz.id}`}>
      <Button variant="outline" size="sm" onClick={() => setDialog('edit')} data-testid={`edit-biz-${biz.id}`}>Edit</Button>
      {suspended
        ? <Button variant="outline" size="sm" onClick={unsuspend} data-testid={`unsuspend-biz-${biz.id}`}>Unsuspend</Button>
        : <Button variant="outline" size="sm" className="text-amber-700" onClick={() => setDialog('suspend')} data-testid={`suspend-biz-${biz.id}`}>Suspend</Button>}
      {!biz.email_verified && <ResendVerificationButton userId={biz.id} headers={headers} testId={`resend-verify-biz-${biz.id}`} />}
      <Button variant="destructive" size="sm" onClick={() => setDialog('delete')} data-testid={`delete-biz-${biz.id}`}>Delete</Button>
      {dialog === 'edit' && <EditBusinessDialog biz={biz} headers={headers} onClose={close} onSaved={done} />}
      {dialog === 'suspend' && <SuspendDialog biz={biz} headers={headers} onClose={close} onDone={done} />}
      {dialog === 'delete' && <DeleteBusinessDialog biz={biz} headers={headers} onClose={close} onDeleted={done} />}
    </div>
  );
};

export const RejectedBusinessesTab = ({ headers, onReapproved }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try { setItems((await axios.get(`${API}/admin/rejected-businesses`, { headers })).data.items || []); }
    catch (e) { toast.error(errMsg(e, 'Failed to load rejected businesses')); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const reapprove = async (id) => {
    try {
      await axios.post(`${API}/admin/approve-business/${id}`, {}, { headers });
      toast.success('Business re-approved'); load(); onReapproved?.();
    } catch (e) { toast.error(errMsg(e, 'Re-approve failed')); }
  };
  if (loading) return <p className="text-sm text-muted-foreground py-6" data-testid="rejected-loading">Loading...</p>;
  if (!items.length) return <p className="text-sm text-muted-foreground py-6" data-testid="rejected-empty">No rejected businesses</p>;
  return (
    <div className="space-y-2" data-testid="rejected-list">
      {items.map(b => (
        <div key={b.id} className="flex items-center justify-between rounded-md border p-3" data-testid={`rejected-biz-${b.id}`}>
          <div>
            <div className="font-medium">{b.company_name || b.full_name} <Badge className="bg-red-100 text-red-700 text-[10px] ml-1">Rejected</Badge></div>
            <p className="text-xs text-muted-foreground">{b.email} · Org {b.org_number || '—'} · Rejected {b.rejected_at ? new Date(b.rejected_at).toLocaleString() : '—'}</p>
            <p className="text-xs">Reason: {b.rejection_reason || '—'}</p>
          </div>
          <Button size="sm" onClick={() => reapprove(b.id)} data-testid={`reapprove-biz-${b.id}`}>Re-approve</Button>
        </div>
      ))}
    </div>
  );
};
